const hasOwn = (value, property) =>
  Object.prototype.hasOwnProperty.call(value, property);

const isPlainObject = (value) => {
  if (value === null || typeof value !== "object") return false;

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const cloneValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneValue);

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        cloneValue(nestedValue),
      ])
    );
  }

  return value;
};

const normalizeNumberInput = (field) => {
  if (field.name === "numberinput") {
    field.name = "Input";
    field.inputType = "number";
  }

  return field;
};

const copyField = (field, copyChild) => {
  if (!isPlainObject(field)) return cloneValue(field);

  const copiedField = Object.fromEntries(
    Object.entries(field).map(([property, value]) => {
      if (property === "container" && Array.isArray(value)) {
        return [property, value.map(copyChild)];
      }

      return [property, cloneValue(value)];
    })
  );

  // The defined-submission endpoint uses `submittedValue`, while the existing
  // form controls consume `value`. Keep both shapes usable without discarding
  // either property.
  if (!hasOwn(copiedField, "value") && hasOwn(copiedField, "submittedValue")) {
    copiedField.value = cloneValue(copiedField.submittedValue);
  }

  return normalizeNumberInput(copiedField);
};

const cloneSubmittedField = (field) =>
  copyField(field, cloneSubmittedField);

const hasKey = (field) =>
  isPlainObject(field) && field.key !== undefined && field.key !== null;

const getAnswerValue = (answer) => {
  if (hasOwn(answer, "value")) {
    return { found: true, value: answer.value };
  }

  if (hasOwn(answer, "submittedValue")) {
    return { found: true, value: answer.submittedValue };
  }

  return { found: false, value: undefined };
};

/**
 * Hydrate a form definition's recursive field structure with saved answers.
 *
 * Fields are matched only by their stable, unique key. Submitted fields that
 * no longer exist in the current definition are appended at the top level so
 * historical answers remain visible.
 */
export const hydrateSubmissionFields = (schemaFields, submissionFields) => {
  const schema = Array.isArray(schemaFields) ? schemaFields : [];
  const answers = Array.isArray(submissionFields)
    ? submissionFields.filter(isPlainObject)
    : [];

  const answersByKey = new Map();
  answers.forEach((answer) => {
    if (hasKey(answer)) answersByKey.set(answer.key, answer);
  });

  const schemaKeys = new Set();
  const collectSchemaKeys = (fields) => {
    fields.forEach((field) => {
      if (!isPlainObject(field)) return;

      if (hasKey(field)) schemaKeys.add(field.key);
      if (Array.isArray(field.container)) collectSchemaKeys(field.container);
    });
  };
  collectSchemaKeys(schema);

  const hydrateField = (field) => {
    const hydratedField = copyField(field, hydrateField);
    if (!isPlainObject(hydratedField) || !hasKey(field)) {
      return hydratedField;
    }

    const answer = answersByKey.get(field.key);
    if (!answer) return hydratedField;

    const answerValue = getAnswerValue(answer);
    if (answerValue.found) {
      hydratedField.value = cloneValue(answerValue.value);
    }

    // Use the submitted type only as a fallback. The schema remains the source
    // of truth whenever it supplies a name.
    if (!hydratedField.name && answer.name) {
      hydratedField.name = answer.name;
      normalizeNumberInput(hydratedField);
    }

    return hydratedField;
  };

  const hydratedSchema = schema.map(hydrateField);
  const unmatchedAnswers = answers
    .filter((answer) => !hasKey(answer) || !schemaKeys.has(answer.key))
    .map(cloneSubmittedField);

  return [...hydratedSchema, ...unmatchedAnswers];
};
